import { Archive, CheckCircle2, ChevronDown, FileEdit, type LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * THE authoring-status pill — Published / Draft / Archived — shared by Workflows and Questionnaires.
 *
 * WHY IT LIVES IN `components/patterns/`. It started inside `WorkflowsListScreen`, and when the
 * questionnaires list needed the same thing it got a second implementation with its own SVG icon set.
 * Two pills for one concept is exactly the drift this folder exists to prevent
 * (`.claude/rules/frontend/features.md`: used in 2+ features → lift it), and the two had already
 * diverged — one used lucide glyphs, the other imported three files of designer artwork to draw the
 * same three states.
 *
 * NO SVG ASSETS. The designer's `Publish.svg` / `Archive.svg` / `Draft.svg` are single-colour
 * outline glyphs that lucide already has (`CheckCircle2`, `Archive`, `FileEdit`), and lucide takes
 * `currentColor` — so the icon follows the pill's status colour with no masking and no files to keep
 * in sync.
 *
 * Styling is the designer's `.sc-dashboard-list__status-badge`: WHITE background, a 1px ring and
 * text in the status colour, and `min-w-102px` with the label pushed left and the chevron right — so
 * all three states are the same width and the column stays aligned. Sizing to content made the
 * shorter labels visibly narrower.
 */

/** The stored values. `Active` is DISPLAYED as "Published" — see the label note below. */
export const STATUS_OPTIONS = ['Active', 'Draft', 'Archived'] as const;

export type AuthoringStatus = (typeof STATUS_OPTIONS)[number];

const STATUS_META: Record<
  AuthoringStatus,
  { label: string; text: string; dot: string; selected: string; Icon: LucideIcon }
> = {
  // The backend value stays "Active"; only the LABEL reads "Published", which is what the admin
  // actually did. Every mutation and comparison keys off the raw status, never off this label —
  // renaming the enum would be a migration across sessions and workflows for a wording change.
  Active: {
    label: 'Published',
    text: 'text-green00',
    dot: 'bg-green00',
    selected: 'text-green00 bg-[#E6F8EB]',
    Icon: CheckCircle2,
  },
  Draft: {
    label: 'Draft',
    text: 'text-gray66',
    dot: 'bg-gray66',
    selected: 'text-gray66 bg-[#F3F4F6]',
    Icon: FileEdit,
  },
  Archived: {
    label: 'Archived',
    text: 'text-orangeDA',
    dot: 'bg-orangeDA',
    selected: 'text-orangeDA bg-[#FFF7ED]',
    Icon: Archive,
  },
};

/** Anything unrecognised reads as Draft — an unknown status must not render an empty pill. */
export function authoringStatusOf(status: string): AuthoringStatus {
  return (STATUS_OPTIONS as readonly string[]).includes(status)
    ? (status as AuthoringStatus)
    : 'Draft';
}

/** "Published" / "Draft" / "Archived" — the label, for toasts and for sorting by what is on screen. */
export function authoringStatusLabel(status: string): string {
  return STATUS_META[authoringStatusOf(status)].label;
}

/**
 * Label, colours and icon for one status.
 *
 * Exported because the workflows list draws its own confirmation dialogs and a bulk-action bar from
 * the same vocabulary. Those read the label and the dot colour without rendering a pill, and having
 * them keep a private copy of the table is how the two implementations drifted in the first place.
 */
export function authoringStatusMeta(status: string) {
  return STATUS_META[authoringStatusOf(status)];
}

export function StatusPill({ status, showChevron }: { status: string; showChevron?: boolean }) {
  const meta = STATUS_META[authoringStatusOf(status)];
  const { Icon } = meta;

  return (
    <span
      className={cn(
        'inline-flex min-w-[102px] items-center justify-between gap-1 rounded-full bg-white px-2 py-[3px] text-[length:var(--text-12)] font-medium ring-1',
        meta.text,
        'ring-current/40',
      )}
    >
      <span className="flex items-center gap-1">
        <Icon className="h-3 w-3 shrink-0" />
        <span>{meta.label}</span>
      </span>
      {showChevron && <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />}
    </span>
  );
}

/**
 * The pill as the control that changes it.
 *
 * The thing showing the status is the thing that changes it, which is what the designer has — and it
 * leaves the row's ⋯ menu for actions that are about the record rather than its lifecycle.
 */
export function StatusPillMenu({
  status,
  onSelect,
  align = 'center',
}: {
  status: string;
  onSelect: (next: AuthoringStatus) => void;
  align?: 'start' | 'center' | 'end';
}) {
  const current = authoringStatusOf(status);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Status: ${STATUS_META[current].label}. Change it.`}
          className="cursor-pointer border-none bg-transparent p-0 transition-opacity hover:opacity-80"
        >
          <StatusPill status={status} showChevron />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-36">
        {STATUS_OPTIONS.map((key) => {
          const option = STATUS_META[key];
          const selected = key === current;
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => !selected && onSelect(key)}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 text-[length:var(--text-12)] font-medium',
                selected && option.selected,
              )}
            >
              {/* A coloured dot in the menu, not the glyph again — the pill above already carries the
                  icon, and repeating it makes the open menu look like three more pills.

                  NO TICK on the selected row. The designer marks the current status by TINTING the
                  row (background + text in its own colour, via `option.selected`), which this already
                  does — so a checkmark beside it says the same thing twice and appears nowhere else
                  in their design. */}
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />
              <span className="flex-1">{option.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
